import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import { useAttendanceContextlessPolicies } from 'modules/attendance/hooks/useAttendanceContextlessPolicies'

export const TYPES = {
  default_export: 'default_export',
  brazil_afd: 'brazil_afd',
  brazil_aej: 'brazil_aej',
  clock_in_out_pdf: 'clock_in_out_pdf',
  full_version: 'full_version',
  full_version_summary: 'full_version_summary',
  workplaces_geolocation: 'workplaces_geolocation',
  pivot_table: 'pivot_table',
}

export const FILE_FORMATS = {
  excel: 'excel',
  pdf: 'pdf',
  txt: 'txt',
}

export const TIME_FORMATS = {
  hours_and_minutes: 'hours_and_minutes',
  hours_in_decimals: 'hours_in_decimals',
  hours_and_minutes_text: 'hours_and_minutes_text',
  minutes: 'minutes',
}

export const TIME_PERIODS = {
  week: 'week',
  month: 'month',
}

export interface Option {
  label: string
  value: string
}

export const useTypeAvailableOptions = ({
  hasBrazilianLegalEntity,
  hasGeoLocationExport,
  clockInOption = false,
}: {
  hasBrazilianLegalEntity?: boolean
  hasGeoLocationExport: boolean
  clockInOption?: boolean
}) => {
  const { brazilianExportAccess } = useAttendanceContextlessPolicies()
  const options: Option[] = []
  const full_version: Option = {
    label: i18n.t('time_tracking.modals.export.options.full_version'),
    value: TYPES.full_version,
  }

  const full_version_summary: Option = {
    label: i18n.t('time_tracking.modals.export.options.full_version_summary'),
    value: TYPES.full_version_summary,
  }

  const pivot_table: Option = {
    label: i18n.t('time_tracking.modals.export.options.pivot_table'),
    value: TYPES.pivot_table,
  }

  const default_export: Option = {
    label: i18n.t('time_tracking.modals.export.options.default_export'),
    value: TYPES.default_export,
  }

  options.push(full_version, full_version_summary, default_export)

  if (hasFeature('dev_pivot_table_export')) {
    options.push(pivot_table)
  }

  if (hasGeoLocationExport) {
    const workplaces_geolocation: Option = {
      label: i18n.t('time_tracking.modals.export.options.workplaces_geolocation'),
      value: TYPES.workplaces_geolocation,
    }
    options.push(workplaces_geolocation)
  }

  if (hasBrazilianLegalEntity) {
    if (brazilianExportAccess) {
      const brazil_afd: Option = {
        label: i18n.t('time_tracking.modals.export.options.brazil_afd'),
        value: TYPES.brazil_afd,
      }

      const brazil_aej: Option = {
        label: i18n.t('time_tracking.modals.export.options.brazil_aej'),
        value: TYPES.brazil_aej,
      }

      options.push(brazil_afd, brazil_aej)
    }

    if (clockInOption) {
      const clock_in_out_pdf: Option = {
        label: i18n.t('time_tracking.modals.export.options.clock_in_out_pdf'),
        value: TYPES.clock_in_out_pdf,
      }

      options.push(clock_in_out_pdf)
    }
  }

  return options
}

export const fileFormatAvailableOptions = (type: string): Option[] => {
  const excel = {
    label: i18n.t('time_tracking.modals.export.options.excel'),
    value: FILE_FORMATS.excel,
  }

  const pdf = {
    label: i18n.t('time_tracking.modals.export.options.pdf'),
    value: FILE_FORMATS.pdf,
  }

  const txt = {
    label: i18n.t('time_tracking.modals.export.options.txt'),
    value: FILE_FORMATS.txt,
  }

  const options: Option[] = []

  switch (type) {
    case TYPES.pivot_table:
      options.push(excel)
      break
    case TYPES.clock_in_out_pdf:
      options.push(pdf)
      break
    case TYPES.default_export:
    case TYPES.full_version:
    case TYPES.full_version_summary:
    case TYPES.workplaces_geolocation:
      options.push(excel, pdf)
      break
    case TYPES.brazil_afd:
    case TYPES.brazil_aej:
      options.push(txt)
      break
  }

  return options
}

export const timeFormatOptions = () => {
  const options: Option[] = []

  const hours_and_minutes: Option = {
    label: i18n.t('time_tracking.modals.export.time_format_options.hours_and_minutes'),
    value: TIME_FORMATS.hours_and_minutes,
  }

  const hours_in_decimals: Option = {
    label: i18n.t('time_tracking.modals.export.time_format_options.hours_in_decimals'),
    value: TIME_FORMATS.hours_in_decimals,
  }

  const hours_and_minutes_text: Option = {
    label: i18n.t('time_tracking.modals.export.time_format_options.hours_and_minutes_text'),
    value: TIME_FORMATS.hours_and_minutes_text,
  }

  const minutes: Option = {
    label: i18n.t('time_tracking.modals.export.time_format_options.minutes'),
    value: TIME_FORMATS.minutes,
  }

  options.push(hours_and_minutes, hours_in_decimals, minutes, hours_and_minutes_text)

  return options
}

export const timePeriodOptions = () => {
  const options: Option[] = []

  const weeklyPeriod: Option = {
    label: i18n.t('time_tracking.modals.export.time_period_options.weekly'),
    value: TIME_PERIODS.week,
  }

  const monthlyPeriod: Option = {
    label: i18n.t('time_tracking.modals.export.time_period_options.monthly'),
    value: TIME_PERIODS.month,
  }

  options.push(weeklyPeriod, monthlyPeriod)

  return options
}
